import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { HandHelping, RefreshCw, MessageSquare, Check, UserCheck } from 'lucide-react'

type Priority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
type Status = 'OPEN' | 'CLAIMED' | 'RESOLVED' | 'CANCELLED'

interface Handoff {
  id: string
  conversationId: string
  reason: string
  summary: string | null
  priority: Priority
  status: Status
  claimedByUserId: string | null
  claimedAt: string | null
  createdAt: string
}

const PRIORITY_ORDER: Record<Priority, number> = { URGENT: 0, HIGH: 1, NORMAL: 2, LOW: 3 }

const PRIORITY_STYLES: Record<Priority, string> = {
  URGENT: 'bg-red-600 text-white',
  HIGH: 'bg-amber-500 text-white',
  NORMAL: 'bg-blueprint-50 text-blueprint-700',
  LOW: 'bg-ink-900/10 text-ink-700/60',
}

const STATUS_STYLES: Record<Status, string> = {
  OPEN: 'bg-blueprint-50 text-blueprint-700',
  CLAIMED: 'bg-amber-50 text-amber-700',
  RESOLVED: 'bg-ink-900/10 text-ink-700/60',
  CANCELLED: 'bg-ink-900/10 text-ink-700/60',
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'ahora mismo'
  if (mins < 60) return `hace ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `hace ${hrs} h`
  const days = Math.floor(hrs / 24)
  return `hace ${days} d`
}

export function HandoffsPage() {
  const [items, setItems] = useState<Handoff[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function load() {
    setErr(null)
    try {
      const data = await api<{ items: Handoff[] }>('/v1/marketing/bots/handoffs')
      // Orden defensivo en cliente (el backend ya ordena, pero por si cambia).
      const sorted = [...data.items].sort((a, b) => {
        const p = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
        if (p !== 0) return p
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      })
      setItems(sorted)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    const id = setInterval(() => void load(), 8000)
    return () => clearInterval(id)
  }, [])

  async function claim(h: Handoff) {
    setBusyId(h.id)
    try {
      // Sprint 8: actor desde JWT verificado en el backend.
      await api(`/v1/marketing/bots/handoffs/${h.id}/claim`, {
        method: 'POST',
      })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function resolve(h: Handoff) {
    setBusyId(h.id)
    try {
      await api(`/v1/marketing/bots/handoffs/${h.id}/resolve`, {
        method: 'POST',
      })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="p-6 max-w-5xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <HandHelping className="size-5 text-blueprint-700" />
            Handoffs
          </h1>
          <p className="text-xs font-mono text-ink-700/60">
            Solicitudes de derivación bot → humano · refresco cada 8 s
          </p>
        </div>
        <button
          type="button"
          className="bp-btn"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw className="size-3.5" />
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </header>

      {err && (
        <div className="mb-3 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {err}
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="bp-card p-10 text-center">
          <p className="text-sm text-ink-700/60">Sin handoffs pendientes.</p>
        </div>
      )}

      <ul className="space-y-3">
        {items.map((h) => (
          <li key={h.id} className="bp-card p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 items-start min-w-[80px]">
                <span className={['bp-pill', PRIORITY_STYLES[h.priority]].join(' ')}>
                  {h.priority}
                </span>
                <span className={['bp-pill', STATUS_STYLES[h.status]].join(' ')}>{h.status}</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-ink-900">{h.reason}</div>
                  <div className="text-[10px] font-mono text-ink-700/60 shrink-0">
                    {formatWhen(h.createdAt)}
                  </div>
                </div>
                {h.summary && (
                  <p className="mt-2 text-xs text-ink-700 whitespace-pre-wrap">{h.summary}</p>
                )}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/marketing/inbox/${h.conversationId}`}
                    className="bp-btn"
                    title="Abrir conversación"
                  >
                    <MessageSquare className="size-3" /> Abrir conversación
                  </Link>
                  {h.status === 'OPEN' && (
                    <button
                      type="button"
                      className="bp-btn"
                      onClick={() => void claim(h)}
                      disabled={busyId === h.id}
                    >
                      <UserCheck className="size-3" /> Tomar
                    </button>
                  )}
                  <button
                    type="button"
                    className="bp-btn-primary"
                    onClick={() => void resolve(h)}
                    disabled={busyId === h.id}
                  >
                    <Check className="size-3" /> Resolver
                  </button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
